import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Filter, Eye, FileText, XCircle, Sparkles, User } from 'lucide-react';
import { getAllMDTReports } from '../services/mdtReportService';
import CustomSelect from '../components/Common/CustomSelect';
import { getReportSummarySnippet } from '../utils/reportUtils';

const Reports: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);

        const reportsData = await getAllMDTReports();
        setReports(reportsData);
      } catch (err) {
        console.error(err);
        setError("Failed to load MDT reports");
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  if (loading) return <p className="p-6 text-gray-500">Loading reports...</p>;
  if (error) return <p className="p-6 text-red-500">{error}</p>;

  const filteredReports = reports.filter(report => {
    const fullName = `${report?.patient?.forename ?? ""} ${report?.patient?.surname ?? ""}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || String(report.id).includes(searchTerm);
    const matchesFilter = filterStatus === 'all' || report.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'final':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'draft':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'generated':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'validated':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'draft':
      case 'generated':
      case 'validated':
      case 'final':
        return <FileText className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">MDT Reports</h1>
      </div>

      {/* Search and Filter */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by patient name or report ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <CustomSelect
              variant="filter"
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'draft', label: 'Draft' },
                { value: 'generated', label: 'Generated' },
                { value: 'validated', label: 'Validated' },
                { value: 'final', label: 'Final' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
              ]}
              placeholder="All Status"
            />
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {filteredReports.map((report) => {
          const patientName = report?.patient
            ? `${report.patient.forename || ''} ${report.patient.surname || ''}`.trim()
            : 'Patient Record';
          const snippet = getReportSummarySnippet(report.context, 180);

          return (
            <div key={report.id} className="card p-5 border border-gray-200 hover:border-primary-300 transition-colors">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-bold text-gray-900">MDT Report #{report?.id}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(report.status)}`}>
                      {getStatusIcon(report.status)}
                      <span className="ml-1 uppercase tracking-wider">{report.status.replace('_', ' ')}</span>
                    </span>
                  </div>

                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <span className="flex items-center space-x-1 font-semibold text-gray-800">
                      <User className="h-3.5 w-3.5 text-gray-400" />
                      <span>{patientName}</span>
                    </span>
                    <span>•</span>
                    <span>Generated by: <strong className="text-gray-800">{report?.created_by?.name || 'Doctor'}</strong></span>
                  </div>

                  {snippet && (
                    <p className="text-xs text-gray-600 bg-gray-50 p-2.5 rounded-lg border border-gray-100 italic line-clamp-2">
                      "{snippet}"
                    </p>
                  )}
                </div>
      
                <div className="flex flex-row md:flex-col items-end justify-between md:justify-center gap-2 flex-shrink-0">
                  <button 
                    onClick={() => navigate(`/reports/${report.id}`, {
                      state: { from: "dashboard" }
                    })}
                    className="btn-primary flex items-center space-x-2 text-xs"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Report</span>
                  </button>

                  <span className="text-xs text-gray-500">
                    {new Date(report.createdAt).toLocaleDateString("en-GB", { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>               
              </div>
            </div>
          );
        })}
      </div>

      {filteredReports.length === 0 && (
        <div className="card text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
        </div>
      )}

      {/* Footer Medical Disclaimer */}
      <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
        <span>
          AI-Assisted Clinical Decision Support — Multidisciplinary consultation reports. Requires clinician review.
        </span>
        <Link
          to="/legal?tab=medical-disclaimer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 underline font-medium whitespace-nowrap"
        >
          Medical Disclaimer &rarr;
        </Link>
      </div>
    </div>
  );
};

export default Reports;
